/**
 * TextInsertionUtil.js
 *
 * Fügt erkannten Diktat-Text in fokussierte Eingabefelder ein
 * (input, textarea, contentEditable) und triggert React onChange via input-Event.
 */

/**
 * @param {string} text
 * @param {boolean} consumeLeadingSpaces
 * @param {number} trailingSpacesCount
 * @returns {string}
 */
export function prepareRecognisedText(text, consumeLeadingSpaces, trailingSpacesCount) {
  if (!text) return '';

  let result = text;
  if (consumeLeadingSpaces) {
    result = result.replace(/^\s+/, '');
  }
  const trailing = Math.max(0, Number(trailingSpacesCount) || 0);
  if (trailing > 0) {
    result += ' '.repeat(trailing);
  }
  return result;
}

/**
 * @param {HTMLElement} element
 * @returns {boolean}
 */
export function isEditableElement(element) {
  if (!element || element.disabled || element.readOnly) return false;

  if (element instanceof HTMLInputElement) {
    const type = (element.type || 'text').toLowerCase();
    return !['button', 'checkbox', 'radio', 'file', 'hidden', 'submit', 'reset', 'image'].includes(type);
  }
  if (element instanceof HTMLTextAreaElement) {
    return true;
  }
  if (element.isContentEditable || element.getAttribute('contenteditable') === 'true') {
    return true;
  }
  return false;
}

/**
 * @param {HTMLInputElement|HTMLTextAreaElement} element
 * @param {string} text
 */
function insertIntoInputElement(element, text) {
  const start = element.selectionStart ?? element.value.length;
  const end = element.selectionEnd ?? element.value.length;
  const before = element.value.slice(0, start);
  const after = element.value.slice(end);
  element.value = before + text + after;
  const cursor = start + text.length;
  element.setSelectionRange(cursor, cursor);
  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * @param {HTMLElement} element
 * @param {string} text
 */
function insertIntoContentEditable(element, text) {
  element.focus();

  const selection = window.getSelection();
  if (!selection) return;

  let range;
  if (selection.rangeCount > 0) {
    range = selection.getRangeAt(0);
    if (!element.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(element);
      range.collapse(false);
    }
  } else {
    range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
  }

  range.deleteContents();
  const textNode = document.createTextNode(text);
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.setEndAfter(textNode);
  selection.removeAllRanges();
  selection.addRange(range);

  element.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * @param {HTMLElement} element
 * @param {string} text
 * @param {{ consumeLeadingSpaces?: boolean, trailingSpacesCount?: number }} [options]
 * @returns {boolean}
 */
export function insertRecognisedText(element, text, options = {}) {
  if (!element || !isEditableElement(element)) {
    return false;
  }

  const prepared = prepareRecognisedText(
    text,
    options.consumeLeadingSpaces,
    options.trailingSpacesCount
  );
  if (!prepared) {
    return false;
  }

  try {
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      insertIntoInputElement(element, prepared);
    } else if (element.isContentEditable || element.getAttribute('contenteditable') === 'true') {
      insertIntoContentEditable(element, prepared);
    } else {
      return false;
    }
    return true;
  } catch (error) {
    console.error('[TextInsertion] Fehler beim Einfügen:', error);
    return false;
  }
}
